import {
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  signInWithPopup,
  signInWithCredential,
  signInWithCustomToken,
  GoogleAuthProvider,
  onAuthStateChanged,
  signOut,
} from "firebase/auth";
import { doc, getDoc, setDoc, serverTimestamp, collection, query, where, getDocs } from "firebase/firestore";
import { auth, db } from "../firebase/config";
import { STORAGE_KEYS, APP_CONFIG } from "../config/appConfig";
import {
  isInRnWebView,
  requestNativeSocialSignIn,
  requestNativeSignOut,
} from "../bridge/webviewBridge";

const googleProvider = new GoogleAuthProvider();

export async function signInWithSocial({ provider, keepLogin = false }) {
  let userCredential;

  if (isInRnWebView()) {
    const result = await requestNativeSocialSignIn(provider);
    if (provider === "google") {
      const credential = GoogleAuthProvider.credential(result.idToken);
      userCredential = await signInWithCredential(auth, credential);
    } else if (provider === "kakao") {
      const customToken = await getKakaoCustomToken(result.accessToken);
      userCredential = await signInWithCustomToken(auth, customToken);
    } else if (provider === "apple") {
      const customToken = await getAppleCustomToken(result.identityToken, result.displayName);
      userCredential = await signInWithCustomToken(auth, customToken);
    }
  } else {
    if (provider === "google") {
      userCredential = await signInWithPopup(auth, googleProvider);
    } else if (provider === "kakao") {
      throw new Error("카카오 로그인은 앱에서만 가능합니다.");
    } else if (provider === "apple") {
      throw new Error("Apple 로그인은 앱에서만 가능합니다.");
    }
  }

  const user = userCredential.user;
  await ensureUserDoc({ uid: user.uid, email: user.email || '', provider });
  // 카카오 Custom Token은 providerData에 안 나오므로 별도 저장
  try { sessionStorage.setItem('lastSocialProvider', provider); } catch {}
  if (keepLogin) saveSession(user.uid);
  return user;
}

async function getAppleCustomToken(identityToken, displayName) {
  const cfUrl = APP_CONFIG.appleCustomTokenUrl;
  if (!cfUrl) throw new Error("Apple 로그인 서비스가 설정되지 않았습니다.");

  const res = await fetch(cfUrl, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ identityToken, displayName: displayName || "" }),
  });
  const data = await res.json();
  if (!res.ok) throw new Error(data.error || "Apple 로그인 실패");
  return data.customToken;
}

async function getKakaoCustomToken(accessToken) {
  const cfUrl = APP_CONFIG.kakaoCustomTokenUrl;
  if (!cfUrl) throw new Error("카카오 로그인 서비스가 설정되지 않았습니다.");

  const res = await fetch(cfUrl, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ accessToken }),
  });
  const data = await res.json();
  if (!res.ok) throw new Error(data.error || "카카오 로그인 실패");
  return data.customToken;
}

export async function signInWithEmailPassword({ email, password, skipEnsure = false }) {
  const cred = await signInWithEmailAndPassword(auth, email, password);
  const { uid } = cred.user;
  if (!skipEnsure) {
    await ensureUserDoc({ uid, email, provider: "email" });
  }
  saveSession(uid);
  return cred.user;
}

export async function signUpWithEmailPassword({ email, password }) {
  const cred = await createUserWithEmailAndPassword(auth, email, password);
  return cred.user;
}

export function watchAuthState(onChange) {
  return onAuthStateChanged(auth, onChange);
}

export async function signOutUser() {
  if (isInRnWebView()) requestNativeSignOut();
  clearSession();
  await signOut(auth);
}

export async function ensureUserDoc({ uid, email, provider }) {
  // 이미 linkedSocialUid 또는 linkedEmailUid로 연결된 기존 사용자가 있으면 스킵
  const linkedQ = query(collection(db, "users"), where("linkedSocialUid", "==", uid));
  const linkedSnap = await getDocs(linkedQ);
  if (!linkedSnap.empty) return;

  const linkedEQ = query(collection(db, "users"), where("linkedEmailUid", "==", uid));
  const linkedESnap = await getDocs(linkedEQ);
  if (!linkedESnap.empty) return;

  const ref = doc(db, "users", uid);
  const snap = await getDoc(ref);
  if (snap.exists()) {
    // 기존 문서가 있으면 누락 필드만 보완
    const data = snap.data();
    const updates = {};
    if (provider && !data.provider) updates.provider = provider;
    if (data.phoneE164 === undefined) updates.phoneE164 = "";
    if (data.phoneVerified === undefined) updates.phoneVerified = false;
    if (!data.createdAt) updates.createdAt = serverTimestamp();
    if (Object.keys(updates).length > 0) {
      updates.updatedAt = serverTimestamp();
      await setDoc(ref, updates, { merge: true });
    }
  }
  // 문서가 없으면 빈 문서를 만들지 않음 — LinkPhonePage에서 전화번호로 기존 계정 연결 또는 새 문서 생성
}

export function saveSession(uid) {
  try {
    sessionStorage.setItem(STORAGE_KEYS.SESSION, JSON.stringify({ uid }));
  } catch { /* noop */ }
}

export function getLastSocialProvider() {
  try { return sessionStorage.getItem('lastSocialProvider') || ''; } catch { return ''; }
}

export function clearSession() {
  try {
    sessionStorage.removeItem(STORAGE_KEYS.SESSION);
    sessionStorage.removeItem('lastSocialProvider');
  } catch { /* noop */ }
}
